import React from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import MarketplaceCta from '@nerdwallet/react-marketplace-cta';
import { useQuery } from '@nerdwallet/apollo';
import analytics from '@nerdwallet/analytics';
import { Button, Link } from '@nerdwallet/currency';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import appConfig from '@nerdwallet/app-config';
import { FinishType } from '@nerdwallet/react-apply-now';
import { withRouter } from 'react-router';
import AppButtons from '../../app-buttons/app-buttons';

import {
  repeatable,
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  group,
} from '../../../lib/editing-configs';
import { getButtonStyle } from '../../../lib/button-style-helper';
import UniversalText from '../../universal-text/universal-text';
import { QUERY } from './query';
import InsuranceButton, { insuranceButtonTypes } from './insurance-button';
import {
  segmentImpressionId,
  trackSegmentTrackEvent,
  trackInteraction,
} from '../../../lib/segment-analytics';
import SegmentImpression from '../../segment-impression';
import { baseSegmentAnalytics } from '../../../lib/utils';
import { Auth0Buttons } from '../../auth0-buttons/auth0-buttons';

export const CUSTOM_BUTTON_TYPE = 'custom';
const APP_STORE_BUTTON_TYPE = 'textMeApp';
const MARKETPLACE_APPLY_NOW = 'marketplace';
const ONBOARDING_GENERIC = 'onboardingGeneric';
const combinedSegmentAnalytics = {
  ...baseSegmentAnalytics,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_name: appConfig?._meta?.deployable,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_version: appConfig?._meta?.version,
};

const addCtaApplyNowLinkTracking = (marketplaceEntity, searchParams) => {
  if (marketplaceEntity?.cta?.link) {
    const updatedUrl = new URL(marketplaceEntity.cta?.link);
    Object.keys(searchParams).forEach((key) => {
      // Prevent to append undefined/null params
      if (searchParams[key]) {
        updatedUrl.searchParams.append(key, searchParams[key]);
      }
    });
    return {
      ...marketplaceEntity,
      cta: {
        ...marketplaceEntity.cta,
        link: updatedUrl.toString(),
      },
    };
  }
  return marketplaceEntity;
};

function buttonTypeInputConfig(fieldName, defaultItemValue = '') {
  const items = [
    {
      label: 'Custom URL',
      value: CUSTOM_BUTTON_TYPE,
    },
    {
      label: 'Dialog: Generic Onboarding',
      value: 'onboardingGeneric',
    },
    {
      label: 'Dialog: NW+',
      value: 'NwPlusOnboarding',
    },
    {
      label: 'App Download Buttons',
      value: APP_STORE_BUTTON_TYPE,
    },
    {
      label: 'Auto Insurance Entry Point',
      value: insuranceButtonTypes.AUTO_INSURANCE,
    },
    {
      label: 'Home Insurance Entry Point',
      value: insuranceButtonTypes.HOME_INSURANCE,
    },
    {
      label: 'Apply Now Button',
      value: MARKETPLACE_APPLY_NOW,
    },
  ];
  let sortedItems = [];
  if (defaultItemValue !== '') {
    sortedItems = items.sort((item) => (item.value === 'custom' ? -1 : 1));
  } else {
    sortedItems = items;
  }

  return stringSelectSpec(`${fieldName}.buttonType`, 'CTA', sortedItems);
}

const CtaButton = ({
  buttonClass,
  buttonType,
  buttonText,
  buttonURL,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  appStoreURL,
  googlePlayURL,
  buttonParams,
  displayStyle,
  link,
  primary,
  disabled,
  loading,
  loadingMessage,
  // eslint-disable-next-line react/prop-types
  dataDependency,
  // eslint-disable-next-line react/prop-types
  impressionId,
  buttonAnnotation,
  pricingCode,
  isInternal,
  defaultZipCode,
  trackClicksViaSegment,
  segment,
  location,
  disablePopback,
  loginCopyOverride,
}) => {
  const offerId = get(dataDependency, 'offerId', null);
  const { data, loading: fetching } = useQuery(QUERY, {
    skip: !offerId,
    variables: { id: offerId },
    fetchPolicy: 'no-cache',
  });
  const productType = data?.marketplaceEntityV2?.productType;
  const sectionName = `${productType}_article_offer`;
  const { pageViewId } = useAnalyticsPageView();

  const isInsuranceButtonType =
    buttonType === insuranceButtonTypes.AUTO_INSURANCE ||
    buttonType === insuranceButtonTypes.HOME_INSURANCE;

  const buttonStyle = getButtonStyle(primary);
  const displayBlock = displayStyle === 'block';

  const params = buttonParams.reduce(
    (paramDict, { queryKey, queryValue }) => ({
      ...paramDict,
      [queryKey]: queryValue,
    }),
    {}
  );

  if (isInsuranceButtonType) {
    return (
      <InsuranceButton
        type={buttonType}
        buttonText={buttonText}
        isInternal={isInternal}
        buttonAnnotation={buttonAnnotation}
        pricingCode={pricingCode}
        defaultZipCode={defaultZipCode || '94103'}
        disablePopback={disablePopback}
      />
    );
  }

  if (buttonType === MARKETPLACE_APPLY_NOW) {
    const marketplaceEntity = {
      ...data?.marketplaceEntityV2,
      cta: {
        ...data?.marketplaceEntityV2?.cta,
        text: buttonText || data?.marketplaceEntityV2?.cta?.text,
      },
      monetizable: 'YES_ASSUMED',
    };

    const marketplaceButtonSegmentImpressionProps = {
      ...combinedSegmentAnalytics,
      impression_id: segmentImpressionId,
      sku_id: marketplaceEntity?.id,
      sku_level: marketplaceEntity?.productType,
      sku_name: marketplaceEntity?.name,
      sku_product_type: marketplaceEntity?.productType,
      sku_source: 'marketplace',
      impression_page_url: location?.pathname,
      impression_pageview_id: pageViewId,
      monetizable: 'YES_ASSUMED',
      sku_impression_id: segmentImpressionId,
      cta_type: 'apply_now_button',
      finish_type: FinishType.EXTERNAL_APPLICATION,
    };

    const updatedMarketPlaceEntity = addCtaApplyNowLinkTracking(
      marketplaceEntity,
      marketplaceButtonSegmentImpressionProps
    );
    const handleMarketplaceCtaClick = () => {
      if (trackClicksViaSegment) {
        trackSegmentTrackEvent('Element Interaction', {
          entity_name: segment?.entityName || 'button',
          location: segment?.location || location?.pathname,
          sub_location: segment?.subLocation || '',
          cta_type: 'apply_now_button',
          ...combinedSegmentAnalytics,
        });
      }
      analytics.track('element_interacted', {
        action: 'select',
        entity_name: sectionName,
        impression_id: impressionId,
        element_name: 'apply_now_button',
        interaction_type: 'press',
        cta_type: 'apply_now_button',
        finish_type: FinishType.EXTERNAL_APPLICATION,
      });
    };

    return fetching ? (
      <p>Loading</p>
    ) : (
      <SegmentImpression
        eventName="MarketplaceCta Viewed"
        eventProps={marketplaceButtonSegmentImpressionProps}
      >
        <MarketplaceCta
          gqlMarketplaceEntity={updatedMarketPlaceEntity}
          analyticsPayloadOverrides={{
            product_impression_id: impressionId,
            section_name: sectionName,
            finish_type: FinishType.EXTERNAL_APPLICATION,
            cta_type: 'apply_now_button',
            baseSegmentAnalytics,
          }}
          onClick={handleMarketplaceCtaClick}
          omitImpression
        />
      </SegmentImpression>
    );
  }

  if (buttonType === APP_STORE_BUTTON_TYPE) {
    return (
      <AppButtons appStoreURL={appStoreURL} googlePlayURL={googlePlayURL} />
    );
  }

  if (buttonType === ONBOARDING_GENERIC) {
    const flowProps = {
      trk: params,
      flowConfig: {},
    };

    if (successURL) {
      flowProps.onSuccess = successURL;
    }

    if (authDialogCopyOverrides) {
      flowProps.flowConfig.copyOverrides = {};
      authDialogCopyOverrides.forEach((copyOverride) => {
        flowProps.flowConfig.copyOverrides[copyOverride.copyKey] =
          copyOverride.copyValue;
      });
    }

    if (authDialogImageOverrides) {
      flowProps.flowConfig.imageOverrides = {};
      authDialogImageOverrides.forEach((imageOverride) => {
        flowProps.flowConfig.imageOverrides[imageOverride.imageKey] =
          imageOverride.imageValue;
      });
    }

    return (
      <Auth0Buttons.Container
        displayBlock={displayBlock}
        loginTrkLocation={segment?.location || location?.pathname}
        loginTrkSubLocation={segment?.subLocation || ''}
      >
        <Auth0Buttons.GenericButton
          redirectTo={successURL}
          customCtaText={buttonText}
          buttonStyle={{ ...buttonStyle }}
          displayBlock={displayBlock}
          className={buttonClass}
          configParams={{
            loginHeading: loginCopyOverride,
            registrationHeading: loginCopyOverride,
          }}
        />
      </Auth0Buttons.Container>
    );
  }

  if (buttonURL) {
    const customButtonSegmentImpressionProps = {
      impression_id: segmentImpressionId,
      sku_id: segment?.entityName,
      sku_level: segment?.entityName,
      sku_name: segment?.entityName,
      sku_product_type: 'cta_button',
      impression_page_url: location?.pathname,
      impression_pageview_id: pageViewId,
      sku_impression_id: segmentImpressionId,
      finish_type: FinishType.EXTERNAL_APPLICATION,
    };

    const handleCampaignLinkClick = () => {
      trackInteraction('Element Interaction', {
        ...baseSegmentAnalytics,
        entity_name: segment?.entityName || 'button',
        location: segment?.location || location?.pathname,
        sub_location: segment?.subLocation || '',
      });
      analytics.track('element_interacted', {
        action: 'select',
        entity_name: segment?.entityName || 'button',
        impression_id: segmentImpressionId,
        interaction_type: 'press',
        sub_location: segment?.subLocation || '',
      });
    };

    return (
      <SegmentImpression
        eventName="CtaButton Viewed"
        eventProps={customButtonSegmentImpressionProps}
      >
        {link ? (
          <Link
            href={buttonURL}
            className={buttonClass}
            onClick={handleCampaignLinkClick}
          >
            {buttonText}
          </Link>
        ) : (
          <Button
            onClick={handleCampaignLinkClick}
            className={buttonClass}
            href={buttonURL}
            block={displayBlock}
            disabled={disabled}
            loading={loading}
            loadingMessage={loadingMessage}
            {...buttonStyle}
          >
            {buttonText}
          </Button>
        )}
      </SegmentImpression>
    );
  }

  return (
    <Button
      className={buttonClass}
      href={buttonURL}
      block={displayBlock}
      disabled={disabled}
      loading={loading}
      loadingMessage={loadingMessage}
      {...buttonStyle}
    >
      {buttonText}
    </Button>
  );
};

CtaButton.displayName = 'CTAButton';

CtaButton.propTypes = {
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonURL: PropTypes.string,
  successURL: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  disablePopback: PropTypes.bool,
  loadingMessage: PropTypes.string,
  authDialogCopyOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      copyKey: PropTypes.string,
      copyValue: PropTypes.string,
    })
  ),
  authDialogImageOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      imageKey: PropTypes.string,
      imageValue: PropTypes.string,
    })
  ),
  buttonParams: PropTypes.array,
  displayStyle: PropTypes.oneOf(['inline-block', 'block']),
  link: PropTypes.bool,
  primary: PropTypes.string,
  appStoreURL: PropTypes.string,
  googlePlayURL: PropTypes.string,
  buttonAnnotation: PropTypes.string,
  pricingCode: PropTypes.string,
  isInternal: PropTypes.bool,
  defaultZipCode: PropTypes.string,
  trackClicksViaSegment: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  segment: PropTypes.shape({
    entityName: PropTypes.string,
    location: PropTypes.string,
    subLocation: PropTypes.string,
  }),
  loginCopyOverride: PropTypes.string,
};

CtaButton.defaultProps = {
  segment: {},
  trackClicksViaSegment: false,
  buttonType: CUSTOM_BUTTON_TYPE,
  buttonParams: [],
  displayStyle: 'inline-block',
  link: false,
  primary: 'primary',
  loading: false,
  disabled: false,
  loadingMessage: '',
  buttonAnnotation: '',
  pricingCode: '',
  isInternal: false,
  defaultZipCode: '94103',
  disablePopback: false,
};

CtaButton.getEditingConfig = (
  componentConfig,
  fieldName,
  allowedControlsList = []
) => {
  const buttonType = get(
    componentConfig,
    `props.${fieldName}.buttonType`,
    CUSTOM_BUTTON_TYPE
  );
  const isAuthDialog = buttonType === ONBOARDING_GENERIC;
  const isMarketplaceType = buttonType === 'marketplace';

  const marketPlaceInputConfig = isMarketplaceType
    ? [stringInputSpec(`${fieldName}.dataDependency.offerId`, 'Offer Id')]
    : [];

  const successURLSpec = isAuthDialog
    ? [
        stringInputSpec(`${fieldName}.successURL`, 'CTA on success url', {
          help: 'For Dialog CTAs, the URL to take the user after they have successfully registered',
        }),
        stringInputSpec(
          `${fieldName}.loginCopyOverride`,
          'Auth Dialog Copy Overrides',
          {
            help: 'The copy displayed at the authentication page!',
          }
        ),
      ]
    : [];

  const appStoreUrlOverridesSpec =
    buttonType === APP_STORE_BUTTON_TYPE ? AppButtons.getEditingConfig() : [];

  const displayStyleEditorControls = allowedControlsList.includes(
    'displayStyle'
  )
    ? [
        stringSelectSpec(
          `${fieldName}.displayStyle`,
          'Display',
          [
            { label: 'Inline', value: 'inline-block' },
            { label: 'Block', value: 'block' },
          ],
          {
            help: 'Defaults to "Inline"',
          }
        ),
      ]
    : [];

  const loadingEditorControls = allowedControlsList.includes('loading')
    ? [boolCheckboxSpec(`${fieldName}.loading`, 'Loading?')]
    : [];

  const disabledEditorControls = allowedControlsList.includes('disabled')
    ? [boolCheckboxSpec(`${fieldName}.disabled`, 'Disabled?')]
    : [];

  const loadingMessageEditorControls = allowedControlsList.includes(
    'loadingMessage'
  )
    ? [stringInputSpec(`${fieldName}.loadingMessage`, 'Loading Message?')]
    : [];

  const alignmentEditorControls = allowedControlsList.includes('alignment')
    ? [
        stringSelectSpec(
          `${fieldName}.alignment`,
          'Alignment',
          [
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ],
          {
            help: 'Defaults to "Left"',
          }
        ),
      ]
    : [];

  const outputButtonTargetUrlInput =
    buttonType === CUSTOM_BUTTON_TYPE
      ? [
          stringInputSpec(
            `${fieldName}.buttonURL`,
            'Custom button target url',
            {
              help: 'URL for the user to be redirected to on click. Be sure to add tracking params (e.g. www.a.com?trk_app=landing-page-client)',
            }
          ),
        ]
      : [];

  const insuranceInputConfig =
    buttonType === insuranceButtonTypes.AUTO_INSURANCE ||
    buttonType === insuranceButtonTypes.HOME_INSURANCE
      ? [
          stringInputSpec(
            `${fieldName}.buttonAnnotation`,
            'CTA Button Annotation'
          ),
          stringInputSpec(`${fieldName}.pricingCode`, 'CTA Pricing Code'),
          stringInputSpec(
            `${fieldName}.defaultZipCode`,
            'CTA Default Zip Code'
          ),
          boolCheckboxSpec(
            `${fieldName}.isInternal`,
            'Use NW Internal Experience?'
          ),
          boolCheckboxSpec(`${fieldName}.disablePopback`, 'Disable Pop-back?'),
        ]
      : [];

  const outputButtonPropertiesInputConfig =
    buttonType === CUSTOM_BUTTON_TYPE
      ? [
          group('Properties', [
            stringSelectSpec(
              `${fieldName}.primary`,
              'Button Style',
              [
                { label: 'Default', value: '' },
                { label: 'Primary', value: 'primary' },
                { label: 'Secondary', value: 'secondary' },
                { label: 'Tertiary', value: 'tertiary' },
              ],
              {
                help: 'Primary output a GREEN button with WHITE text; Secondary output a WHITE button with GREEN text, Tertiary output a WHITE button without border',
              }
            ),
            ...disabledEditorControls,
            ...loadingEditorControls,
            ...loadingMessageEditorControls,
            ...displayStyleEditorControls,
            ...alignmentEditorControls,
          ]),
        ]
      : [];

  const trackClicksViaSegmentInputConfig = get(
    componentConfig,
    `props.${fieldName}.trackClicksViaSegment`,
    false
  )
    ? [
        stringInputSpec(`${fieldName}.segment.entityName`, 'Entity Name'),
        stringInputSpec(`${fieldName}.segment.location`, 'Location'),
        stringInputSpec(`${fieldName}.segment.subLocation`, 'Sub Location'),
      ]
    : [];

  return [
    buttonTypeInputConfig(fieldName, buttonType ? '' : CUSTOM_BUTTON_TYPE),
    stringTextAreaSpec(`${fieldName}.buttonText`, 'CTA Text'),
    ...successURLSpec,
    ...appStoreUrlOverridesSpec,
    ...outputButtonTargetUrlInput,
    ...marketPlaceInputConfig,
    ...insuranceInputConfig,
    ...outputButtonPropertiesInputConfig,

    ...UniversalText.getEditingConfig(
      componentConfig,
      'Extra Text',
      [
        'textSize',
        'textType',
        'fontWeight',
        'padding',
        'margin',
        'groupButton',
        'fontFamily',
        'textColor',
      ],
      `${fieldName}.ctaExtraText`,
      { text: { textType: 'text' } }
    ),

    group('Tracking', [
      boolCheckboxSpec(
        `${fieldName}.trackClicksViaSegment`,
        'Track clicks via Segment?'
      ),
      ...trackClicksViaSegmentInputConfig,
      stringInputSpec(`${fieldName}.componentName`, 'Component Name', {
        help: 'For Tracking Purposes',
      }),
      repeatable(
        `${fieldName}.buttonParams`,
        'Tracking Params',
        'Param',
        () => [
          stringInputSpec(`queryKey`, 'Key'),
          stringTextAreaSpec(`queryValue`, 'Value'),
        ]
      ),
    ]),
  ];
};

export default withRouter(CtaButton);
